import { RestrictToVerticalAxis } from "@dnd-kit/abstract/modifiers";
import { DragDropProvider, KeyboardSensor, PointerSensor } from "@dnd-kit/react";
import { useSortable } from "@dnd-kit/react/sortable";
import { Button } from "@repo/ui/components/ui/button";
import { InputField, NumberField, TextareaField } from "@repo/ui/form-fields";
import { cn } from "@repo/ui/lib/utils";
import { AlignLeft, GripVertical, Trash2 } from "lucide-react";
import { useState } from "react";
import { AddCatalogItemButton } from "@/shared/common/add-catalog-item-button";
import type { DraftAnswerOption } from "./types";

const optionSensors = [
  PointerSensor.configure({
    activatorElements(source) {
      return source.handle ? [source.handle] : [source.element];
    },
  }),
  KeyboardSensor,
];

interface AnswerOptionsEditorProps {
  options: DraftAnswerOption[];
  scoringEnabled: boolean;
  pointsDisabledReason?: string;
  onAddOption: () => void;
  onRemoveOption: (index: number) => void;
  onUpdateOption: (index: number, option: DraftAnswerOption) => void;
  onMoveOption: (from: number, to: number) => void;
}

export function AnswerOptionsEditor({
  options,
  scoringEnabled,
  pointsDisabledReason,
  onAddOption,
  onRemoveOption,
  onUpdateOption,
  onMoveOption,
}: AnswerOptionsEditorProps) {
  const hasPoints = options.some((option) => option.points !== undefined);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set());
  const toggleDetail = (clientId: string) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(clientId)) next.delete(clientId);
      else next.add(clientId);
      return next;
    });
  return (
    <div className="space-y-2">
      <h4 className="text-sm font-medium">Answer Options</h4>
      {scoringEnabled && pointsDisabledReason && (
        <p
          role={hasPoints ? "alert" : undefined}
          className={hasPoints ? "text-xs text-destructive" : "text-xs text-muted-foreground"}
        >
          {pointsDisabledReason}
        </p>
      )}

      <div className="space-y-4 pt-2">
        <DragDropProvider
          sensors={optionSensors}
          // biome-ignore lint/suspicious/noExplicitAny: dnd-kit event types
          onDragEnd={(event: any) => {
            const sortable = event.operation.source?.sortable;
            if (event.canceled || !sortable || sortable.initialIndex === sortable.index) return;
            onMoveOption(sortable.initialIndex, sortable.index);
          }}
        >
          {options.map((option, index) => {
            const displayValue = option.label;
            const detailOpen = expanded.has(option.clientId);
            const hasDetail = !!option.detail?.trim();
            return (
              <SortableOption
                key={option.clientId}
                id={option.clientId}
                index={index}
                below={
                  detailOpen && (
                    <div className="pb-2 pl-[1.375rem]">
                      <TextareaField
                        placeholder={`Option ${index + 1} detail`}
                        value={option.detail ?? ""}
                        onBlur={() => {}}
                        onChange={(detail) => onUpdateOption(index, { ...option, detail: detail || undefined })}
                      />
                    </div>
                  )
                }
              >
                {(handleRef) => (
                  <>
                    <div
                      ref={handleRef}
                      className="flex shrink-0 cursor-grab items-center text-muted-foreground hover:text-foreground"
                    >
                      <GripVertical className="size-4" />
                    </div>
                    <div className="min-w-0 flex-[2]">
                      <InputField
                        ariaLabel={`Option ${index + 1}`}
                        onBlur={() => {}}
                        value={displayValue}
                        placeholder={`Option ${index + 1}`}
                        onChange={(value) => onUpdateOption(index, { ...option, label: value })}
                      />
                    </div>
                    {scoringEnabled && (
                      <div className="w-32 shrink-0">
                        <NumberField
                          ariaLabel={`Option ${index + 1} points`}
                          disabled={!!pointsDisabledReason && !hasPoints}
                          value={option.points ?? null}
                          step={1}
                          onBlur={() => {}}
                          onChange={(points) => onUpdateOption(index, { ...option, points: points ?? undefined })}
                        />
                      </div>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      className={cn("size-7 shrink-0", hasDetail ? "text-foreground" : "text-muted-foreground")}
                      aria-label={detailOpen ? `Hide option ${index + 1} detail` : `Show option ${index + 1} detail`}
                      aria-expanded={detailOpen}
                      onClick={() => toggleDetail(option.clientId)}
                    >
                      <AlignLeft className={cn("size-3", hasDetail && "stroke-[3]")} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-7 shrink-0 text-destructive hover:text-destructive"
                      onClick={() => onRemoveOption(index)}
                    >
                      <Trash2 className="size-3" />
                    </Button>
                  </>
                )}
              </SortableOption>
            );
          })}
        </DragDropProvider>
        <AddCatalogItemButton label="Option" onClick={onAddOption} />
      </div>
    </div>
  );
}

function SortableOption({
  id,
  index,
  below,
  children,
}: {
  id: string;
  index: number;
  below?: React.ReactNode;
  children: (handleRef: (node: HTMLElement | null) => void) => React.ReactNode;
}) {
  const { ref, handleRef, isDragging } = useSortable({ id, index, modifiers: [RestrictToVerticalAxis] });
  return (
    <div ref={ref} className={cn("space-y-1.5", isDragging && "opacity-50")}>
      <div className="flex items-center gap-1.5">{children(handleRef)}</div>
      {below}
    </div>
  );
}
